import { ChevronRight } from 'lucide-react';
import './Breadcrumb.css';

export type BreadcrumbItem = {
    label: string;
    onClick?: () => void;
};

type Props = {
    items: BreadcrumbItem[];
    /** Style the first item with header-title weight/size so it reads like the page title */
    titleFirst?: boolean;
    /** Style the last item (current page) with header-title weight/size as the section title */
    currentTitle?: boolean;
    /**
     * Style the first item as a page-title heading (20px/600/brand-font) while
     * keeping its link colour. Use this when the breadcrumb replaces an <h1>
     * and the first segment should be prominently sized like a title.
     */
    headerTitle?: boolean;
};

/**
 * Breadcrumb navigation trail.
 * Reference: Coursera course > lesson path.
 *
 * Last item is the current page (not clickable).
 * All preceding items are clickable links.
 */
const MAX_ITEMS = 3;

function Breadcrumb({ items, titleFirst, currentTitle, headerTitle }: Props) {
    if (items.length <= 1) return null;

    // Always show at most MAX_ITEMS (3). If the trail is longer, keep the first
    // item (root) and the last 2 items so the user always sees where they are.
    const visible: BreadcrumbItem[] =
        items.length > MAX_ITEMS
            ? [items[0], ...items.slice(-(MAX_ITEMS - 1))].filter(
                  (x): x is BreadcrumbItem => !!x,
              )
            : items;

    const listClass = [
        'ui-breadcrumb__list',
        titleFirst ? 'ui-breadcrumb__list--title-first' : '',
        currentTitle ? 'ui-breadcrumb__list--current-title' : '',
        headerTitle ? 'ui-breadcrumb__list--header-title' : '',
    ].filter(Boolean).join(' ');

    return (
        <nav className="ui-breadcrumb" aria-label="Breadcrumb">
            <ol className={listClass}>
                {visible.map((item, i) => {
                    const isLast = i === visible.length - 1;
                    return (
                        <li key={i} className="ui-breadcrumb__item">
                            {!isLast && item.onClick ? (
                                <button
                                    type="button"
                                    className="ui-breadcrumb__link"
                                    onClick={item.onClick}
                                >
                                    {item.label}
                                </button>
                            ) : (
                                <span
                                    className="ui-breadcrumb__current"
                                    aria-current="page"
                                >
                                    {item.label}
                                </span>
                            )}
                            {!isLast && (
                                <ChevronRight size={14} className="ui-breadcrumb__sep" aria-hidden="true" />
                            )}
                        </li>
                    );
                })}
            </ol>
        </nav>
    );
}

export default Breadcrumb;
